import { useState, useEffect, useCallback } from 'react'
import { MergedConfigResult, ConfigType } from '../types/config'
import { configApi } from '../services/configApi'

interface UseTableConfigReturn {
  tableConfig: MergedConfigResult | null
  loadingTableConfig: boolean
  errorTableConfig: Error | null
  reloadTableConfig: () => Promise<void>
}

const DEFAULT_ROUTE_JOB = '__root__'

export const useTableConfig = (
  customer?: string | null,
  job?: string | null,
  tablePath?: string | null
): UseTableConfigReturn => {
  const [tableConfig, setTableConfig] = useState<MergedConfigResult | null>(null)
  const [loadingTableConfig, setLoadingTableConfig] = useState(false)
  const [errorTableConfig, setErrorTableConfig] = useState<Error | null>(null)

  const fetchTableConfig = useCallback(async (): Promise<void> => {
    // Don't fetch if required params are missing
    if (!customer || !job || !tablePath) {
      setTableConfig(null)
      setLoadingTableConfig(false)
      setErrorTableConfig(null)
      return
    }

    try {
      setLoadingTableConfig(true)
      setErrorTableConfig(null)

      // Parse table path to extract folder and table name
      const folderPath = tablePath.substring(0, tablePath.lastIndexOf('/')) || '/'
      const tableName = tablePath.split('/').pop()?.replace(/\.cbt$/, '') || ''

      const routeJob = job || DEFAULT_ROUTE_JOB

      const merged = await configApi.getMergedConfig(customer, routeJob, {
        config_type: ConfigType.Table,
        customer,
        job,
        folder_path: folderPath,
        table_name: tableName + '.cbt'
      })

      setTableConfig(merged)
    } catch (err) {
      console.error('Error loading table config:', err)
      setErrorTableConfig(err instanceof Error ? err : new Error('Failed to load table config'))
      setTableConfig(null)
    } finally {
      setLoadingTableConfig(false)
    }
  }, [customer, job, tablePath])

  // Fetch config when parameters change
  useEffect(() => {
    fetchTableConfig()
  }, [fetchTableConfig])

  const reloadTableConfig = useCallback(async (): Promise<void> => {
    await fetchTableConfig()
  }, [fetchTableConfig])

  return {
    tableConfig,
    loadingTableConfig,
    errorTableConfig,
    reloadTableConfig
  }
}

export default useTableConfig
